import React, { useState, useEffect, FC } from "react";
import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Badge,
  Modal,
  ModalBody,
  Row,
  Col,
} from "reactstrap";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Button,
  Icon,
  PaginationComponent,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  TooltipComponent,
} from "../../../components/Component";
import Swal from "sweetalert2";
import workCategoryApi from "../../../api/master/workCategory";
import { useSelector } from "react-redux";
import moment from "moment";
import AddModal from "./AddModalWorkCategory";
import EditModal from "./EditModalWorkCategory";
import Export from "../../../components/export/Export";
import useSequence from "../../../hooks/useSequence";

interface WorkCategoryItem {
  id: string;
  referenceID: string;
  workCategoryID: string;
  workCategoryName: string;
  categoryName: string;
  description?: string;
  status: string;
  createdDate: string;
}

const WorkCategory: FC = () => {
  const { hasSequence, refetch: refetchHasSequence } = useSequence(170);
  const [isLoading, setIsLoading] = useState(true);
  const { data: accessibility } = useSelector((state: any) => state.accessibility);
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });
  const [onSearchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [sort, setSortState] = useState("");
  const [sm, updateSm] = useState(false);

  const [modal, setModal] = useState({
    edit: false,
    add: false,
  });
  const [viewModal, setViewModal] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [],
  });
  const [editId, setEditedId] = useState<string>("");
  const [detail, setDetail] = useState<WorkCategoryItem | null>(null);
  const [formData, setFormData] = useState({
    referenceID: "",
    categoryName: "",
    description: "",
    workCategoryID: "",
  });
  const [editFormData, setEditFormData] = useState({
    referenceID: "",
    categoryName: "",
    description: "",
    workCategoryID: "",
    status: "Active",
  });
  const [rowData, setRowData] = useState<WorkCategoryItem[]>([]);
  const [originalData, setOriginalData] = useState<WorkCategoryItem[]>([]);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = rowData.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const loadDetail = (id: string) => {
    const item = rowData.find((item) => item.id === id);
    setDetail(item || null);
  };

  const closeModal = () => {
    setModal({ ...modal, add: false });
    resetForm();
    setAlertInfo({
      type: "",
      message: "",
      errors: [],
    });
  };

  const closeEditModal = () => {
    setModal({ ...modal, edit: false });
    setAlertInfo({
      type: "",
      message: "",
      errors: [],
    });
  };

  const resetForm = () => {
    setFormData({
      referenceID: "",
      categoryName: "",
      description: "",
      workCategoryID: "",
    });
  };

  const onFormSubmit = async (submitData: any) => {
    const payload = { ...submitData };
    if (hasSequence) {
      delete payload.referenceID;
    }
    const response = await workCategoryApi.createWorkCategory(submitData);
    const { status, message } = response;
    if (status) {
      Swal.fire("Created!", "Work Category has been created", "success");
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
      closeModal();
      fetchData();
    } else {
      let errorMessage = "An error occurred";
      const fullErrorMessage = `${errorMessage}\n${message.join(", ")}`;
      setAlertInfo({
        type: "error",
        message: fullErrorMessage,
        errors: [],
      });
    }
  };

  const onEditSubmit = async (submitData: any) => {
    const response = await workCategoryApi.updateWorkCategoryById(editId, submitData);
    const { status, message } = response;
    if (status) {
      Swal.fire("Updated!", "Work Category has been updated", "success");
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
      setModal({ ...modal, edit: false });
      fetchData();
    } else {
      let errorMessage = "An error occurred";
      const fullErrorMessage = `${errorMessage}\n${message.join(", ")}`;
      setAlertInfo({
        type: "error",
        message: fullErrorMessage,
        errors: [],
      });
    }
  };

  const onEditClick = (id: string) => {
    const item = rowData.find((item) => item.id === id);
    if (item) {
      setEditFormData({
        referenceID: item.referenceID,
        workCategoryID: item.workCategoryID,
        categoryName: item.categoryName,
        description: item.description || "",
        status: item.status || "Active",
      });
      setEditedId(id);
      setModal({ ...modal, edit: true });
    }
  };

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } = accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data, status, message } = await workCategoryApi.fetchWorkCategories();
      if (status) {
        setRowData(data);
        setOriginalData(data);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (onSearchText !== "") {
      const searchText = onSearchText.toLowerCase();
      const filteredData = originalData.filter((item) => {
        return Object.values(item).some((value) => {
          return value && value.toString().toLowerCase().includes(searchText);
        });
      });
      setRowData(filteredData);
    } else {
      setRowData(originalData);
    }
  }, [onSearchText, originalData]);

  const sortFunc = (params: any) => {
    let defaultData = rowData;
    if (params === "asc") {
      let sortedData = defaultData.sort((a: any, b: any) =>
        a.createdDate.localeCompare(b.createdDate)
      );
      setRowData([...sortedData]);
    } else if (params === "dsc") {
      let sortedData = defaultData.sort((a: any, b: any) =>
        b.createdDate.localeCompare(a.createdDate)
      );
      setRowData([...sortedData]);
    }
  };

  return (
    <>
      <Head title="Work Category"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Work Category
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {rowData.length} Work Categories.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${sm ? "active" : ""
                    }`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="menu-alt-r"></Icon>
                </Button>
                <div
                  className="toggle-expand-content"
                  style={{ display: sm ? "block" : "none" }}
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <Export data={rowData} />
                    </li>
                    {allowedActions?.add && (
                      <li className="nk-block-tools-opt">
                        <Button
                          color="primary"
                          onClick={async () => {
                            try {
                              if (typeof refetchHasSequence === "function") {
                                await refetchHasSequence();
                              }
                            } catch (err) {
                              console.error("hasSequence refetch failed", err);
                            }

                            setAlertInfo({
                              type: "",
                              message: "",
                              errors: [],
                            });
                            setModal({ ...modal, add: true });
                          }}
                        >
                          <Icon name="plus"></Icon>
                          <span>Add</span>
                        </Button>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <DataTable
            className="card-stretch"
            bodyClassName={undefined}
            title={undefined}
          >
            <div className="card-inner position-relative card-tools-toggle">
              <div className={`card-search search-wrap active`}>
                <div className="card-body">
                  <div className="search-content" style={{ whiteSpace: "nowrap", display: "flex" }}>
                    <Button
                      className="search-back btn-icon toggle-search active"
                    >
                      <Icon name="search"></Icon>
                    </Button>
                    <input
                      type="text"
                      className="border-transparent form-focus-none form-control"
                      placeholder="Search"
                      value={onSearchText}
                      onChange={(e) => onFilterChange(e)}
                    />
                    <UncontrolledDropdown>
                      <DropdownToggle
                        tag="a"
                        className="btn btn-trigger btn-icon dropdown-toggle"
                      >
                        <Icon name="filter"></Icon>
                      </DropdownToggle>
                      <DropdownMenu end className="dropdown-menu-xs">
                        <ul className="link-check">
                          <li>
                            <span>Show</span>
                          </li>
                          <li className={itemPerPage === 10 ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setItemPerPage(10);
                              }}
                            >
                              10
                            </DropdownItem>
                          </li>
                          <li className={itemPerPage === 15 ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setItemPerPage(15);
                              }}
                            >
                              15
                            </DropdownItem>
                          </li>
                        </ul>
                        <ul className="link-check">
                          <li>
                            <span>Order</span>
                          </li>
                          <li className={sort === "dsc" ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setSortState("dsc");
                                sortFunc("dsc");
                              }}
                            >
                              DESC
                            </DropdownItem>
                          </li>
                          <li className={sort === "asc" ? "active" : ""}>
                            <DropdownItem
                              tag="a"
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setSortState("asc");
                                sortFunc("asc");
                              }}
                            >
                              ASC
                            </DropdownItem>
                          </li>
                        </ul>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                </div>
              </div>
            </div>

            <DataTableBody compact className={undefined} bodyclass={undefined}>
              <DataTableHead>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">Work Category ID</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">Work Type</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">Category Name</span>
                </DataTableRow>
                <DataTableRow size="sm" className={undefined}>
                  <span className="sub-text">Description</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">Created Date</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  <span className="sub-text">Status</span>
                </DataTableRow>
                <DataTableRow size="md" className={undefined}>
                  {/* <span className="sub-text">Details</span> */}
                </DataTableRow>
              </DataTableHead>
              {isLoading ? (
                <div className="text-center p-4">
                  <span className="text-silent">Loading...</span>
                </div>
              ) : currentItems.length > 0 ? (
                currentItems.map((item: any, index: number) => {
                  return (
                    <DataTableItem
                      key={item.id}
                      className={undefined}
                      style={{ color: "black" }}
                    >
                      <DataTableRow size="md" className={undefined}>
                        <span>{item.referenceID}</span>
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <span>{item.workCategoryName}</span>
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <span>{item.categoryName}</span>
                      </DataTableRow>
                      <DataTableRow size="sm" className={undefined}>
                        <span id={`desc-tooltip-${index}`}>
                          {item.description && item.description.length > 25
                            ? `${item.description.substring(0, 25)}...`
                            : item.description || "-"}
                        </span>
                        {item.description && item.description.length > 25 && (
                          <TooltipComponent
                            iconClass="info-circle text-info"
                            icon="info"
                            id={`desc-tooltip-${index}`}
                            direction="top"
                            text={item.description}
                            containerClassName="d-inline-block ms-1"
                          />
                        )}
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <span>{moment(item.createdDate).format("DD-MMM-YYYY")}</span>
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <span
                          className={`tb-status text-${item.status === "Active" ? "success" : "danger"
                            }`}
                        >
                          {item.status}
                        </span>
                      </DataTableRow>
                      <DataTableRow size="md" className={undefined}>
                        <ul className="nk-tb-actions">
                          {allowedActions?.edit && (
                            <li
                              className="nk-tb-action-hidden"
                              onClick={() => onEditClick(item.id)}
                            >
                              <TooltipComponent
                                tag="a"
                                containerClassName="btn btn-trigger btn-icon"
                                id={"edit" + item.id}
                                icon="edit-alt-fill"
                                direction="top"
                                text="Edit"
                                iconClass={undefined}
                              />
                            </li>
                          )}
                          <li>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                tag="a"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                              >
                                <Icon name="more-h"></Icon>
                              </DropdownToggle>
                              <DropdownMenu end>
                                <ul className="link-list-opt no-bdr">
                                  <li>
                                    <Link
                                      to="#"
                                      onClick={() => {
                                        loadDetail(item.id);
                                        setViewModal(true);
                                      }}
                                    >
                                      <Icon name="focus"></Icon>
                                      <span>View Details</span>
                                    </Link>
                                  </li>
                                  {allowedActions?.edit && (
                                    <li onClick={() => onEditClick(item.id)}>
                                      <DropdownItem
                                        tag="a"
                                        href="#edit"
                                        onClick={(ev) => ev.preventDefault()}
                                      >
                                        <Icon name="edit" />
                                        <span>Edit</span>
                                      </DropdownItem>
                                    </li>
                                  )}
                                </ul>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  );
                })
              ) : (
                <div className="text-center p-4">
                  <span className="text-silent">No data found</span>
                </div>
              )}
            </DataTableBody>
            <div className="card-inner">
              {currentItems.length > 0 ? (
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={rowData.length}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              ) : null}
            </div>
          </DataTable>
        </Block>
      </Content>

      <AddModal
        modal={modal.add}
        formData={formData}
        setFormData={setFormData}
        closeModal={closeModal}
        onSubmit={onFormSubmit}
        filterStatus
        alertInfo={alertInfo}
      />
      <EditModal
        modal={modal.edit}
        formData={editFormData}
        setFormData={setEditFormData}
        closeModal={closeEditModal}
        onSubmit={onEditSubmit}
        filterStatus
        alertInfo={alertInfo}
      />

      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        className="modal-dialog-centered"
        size="lg"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setViewModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="nk-modal-head">
            <h4 className="nk-modal-title title">
              Work Category Details -{" "}
              <small className="text-primary"> {detail?.referenceID}</small>
            </h4>
          </div>
          <div className="nk-tnx-details mt-sm-3">
            <Row className="gy-3">
              <Col lg={6}>
                <span className="sub-text">Work Category ID</span>
                <span className="caption-text">{detail?.referenceID}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Work Type</span>
                <span className="caption-text text-break">
                  {detail?.workCategoryName}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Category Name</span>
                <span className="caption-text">{detail?.categoryName}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Status</span>
                <Badge
                  color={detail?.status === "Active" ? "success" : "danger"}
                  size="md"
                >
                  {detail?.status}
                </Badge>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Description</span>
                <span className="caption-text"> {detail?.description}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created Date</span>
                <span className="caption-text">
                  {" "}
                  {detail?.createdDate && moment(detail.createdDate).format("DD MMM YYYY")}
                </span>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default WorkCategory;